import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';

export const Header = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/products', label: t('nav.solutions') },
    { path: '/gallery', label: t('nav.products') },
    { path: '/partners', label: t('nav.partners') },
    { path: '/contacts', label: t('nav.contacts') }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img src="/images/pura-logo.png" alt="PURA Ukraine" className="h-12 w-12" />
            <div className="flex flex-col">
              <span className="text-2xl font-bold" style={{ color: 'rgb(1, 157, 180)' }}>PURA</span>
              <span className="text-xs text-gray-600">Ukraine</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-[rgb(1,157,180)] ${
                  location.pathname === link.path
                    ? 'text-[rgb(1,157,180)]'
                    : 'text-gray-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Contact Button & Language Switcher */}
          <div className="hidden lg:flex items-center space-x-4">
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-2 text-gray-700 hover:text-[rgb(1,157,180)] transition-colors px-3 py-2 rounded-md hover:bg-gray-100"
              aria-label="Switch language"
            >
              <Globe className="h-4 w-4" />
              <span className="text-sm font-medium uppercase">{language}</span>
            </button>
            <div className="flex flex-col text-right">
              <a href="tel:+380689220226" className="text-gray-700 hover:text-[rgb(1,157,180)] transition-colors text-sm font-medium">
                +380 68 922 02 26
              </a>
            </div>
            <Button
              asChild
              style={{ backgroundColor: 'rgb(1, 157, 180)' }}
              className="text-white hover:opacity-90 transition-opacity"
            >
              <Link to="/contacts">{t('hero.consultation')}</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t">
          <nav className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'bg-[rgb(1,157,180)] text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 border-t space-y-2">
              <a
                href="tel:+380689220226"
                className="flex items-center justify-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                <Phone className="h-4 w-4" />
                <span className="text-sm font-medium">+380 68 922 02 26</span>
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};